import { JSX, ReactNode } from 'react';

import { useField } from 'react-final-form';

import {
  FileInputWithCrop as Input,
  FileInputValue,
  AspectRatioOption,
} from '@vk-hr-tek/ui/input';

import { composeValidators, ValidateType } from '../validation';

/**
 * Свойства компонента {@link FileInputWithCrop}
 */
type FileInputWithCropProps = {
  /**
   * Подпись поля.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode;

  /**
   * Имя поля.
   */
  name: string;

  /**
   * Вариант отображения поля.
   *
   * @defaultValue `'input'`
   */
  variant?: 'input' | 'preview';

  /**
   * Текст заполнителя.
   */
  placeholder?: string;

  /**
   * Обязательность поля.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Недоступность поля.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Обработчик изменения значения.
   */
  onChange?: (value?: FileInputValue) => void;

  /**
   * Состояние загрузки.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Текст при загрузке.
   */
  loadingText?: JSX.Element;

  /**
   * Показывать ошибку без взаимодействия с полем.
   *
   * @defaultValue `false`
   */
  showErrorWithoutTouched?: boolean;

  /**
   * Массив валидаторов.
   */
  validate?: ValidateType[];

  /**
   * Функция для кэширования файла.
   */
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;

  /**
   * Занимать всю ширину контейнера.
   */
  fullWidth?: boolean;

  /**
   * Текст баннера.
   */
  bannerText?: string;

  /**
   * Флаг распознавания QR-кода.
   *
   * @defaultValue `false`
   */
  autorecognitionQr?: boolean;

  /**
   * Дополнительный текст помощи.
   */
  helperText?: string;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;

  /**
   * Флаг авторизации.
   */
  withAuth?: boolean;

  /**
   * Разрешенные типы файлов.
   */
  allowedTypes?: string[];
  aspectRatioOptions?: AspectRatioOption[];
  dialogTitle?: string;
  dialogDescription?: string;
  sizeAspect?: number;
};

/**
 * Компонент для загрузки файла с возможностью обрезать миниатюру и предварительно посмотреть изображение с интеграцией в react-final-form.
 *
 * @public
 * @param label - Подпись поля.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка.
 * @param name - Имя поля.
 * @param variant - Вариант отображения поля.
 * @param placeholder - Текст заполнителя.
 * @param required - Обязательность поля.
 * @param disabled - Недоступность поля.
 * @param onChange - Обработчик изменения значения.
 * @param loading - Состояние загрузки.
 * @param loadingText - Текст при загрузке.
 * @param showErrorWithoutTouched - Показывать ошибку без взаимодействия с полем.
 * @param validate - Массив валидаторов.
 * @param cache - Функция для кэширования файла.
 * @param fullWidth - Занимать всю ширину контейнера.
 * @param bannerText - Текст баннера.
 * @param autorecognitionQr - Флаг распознавания QR-кода.
 * @param helperText - Дополнительный текст помощи.
 * @param testId - Идентификатор для тестирования.
 * @param withAuth - Флаг авторизации.
 * @param allowedTypes - Разрешенные типы файлов.
 * @param aspectRatioOptions - Варианты соотношения сторон, доступные при кадрировании.
 * @param dialogTitle - Заголовок диалога кадрирования.
 * @param dialogDescription - Описание в диалоге кадрирования.
 * @param sizeAspect - Соотношение сторон области предпросмотра.
 * @returns Компонент для загрузки файла с обрезкой.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FileInputWithCropProps}
 */
export const FileInputWithCrop = ({
  label,
  labelIcon,
  tooltip,
  name,
  variant = 'input',
  placeholder,
  required = false,
  disabled = false,
  onChange,
  loading = false,
  loadingText = undefined,
  showErrorWithoutTouched = false,
  validate,
  cache,
  fullWidth,
  bannerText,
  autorecognitionQr = false,
  helperText,
  testId,
  withAuth,
  allowedTypes,
  aspectRatioOptions,
  dialogTitle,
  dialogDescription,
  sizeAspect,
}: FileInputWithCropProps) => {
  const validators = composeValidators(validate || []);
  const { input, meta } = useField(name, { validate: validators });

  const { onChange: onChangeInput, onBlur } = input;

  const error = showErrorWithoutTouched
    ? !!input.value && meta.error
    : meta.touched && meta.error;

  return (
    <Input
      {...input}
      label={label}
      labelIcon={labelIcon}
      value={input.value}
      onChange={(props) => {
        onChangeInput(props);
        onChange?.(props);
      }}
      variant={variant}
      tooltip={tooltip}
      required={required}
      error={error}
      errorText={meta.error}
      helperText={helperText}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      onBlur={onBlur}
      cache={cache}
      fullWidth={fullWidth}
      placeholder={placeholder}
      autorecognitionQr={autorecognitionQr}
      bannerText={bannerText}
      testId={testId}
      withAuth={withAuth}
      validators={validators}
      allowedTypes={allowedTypes}
      aspectRatioOptions={aspectRatioOptions}
      dialogTitle={dialogTitle}
      dialogDescription={dialogDescription}
      sizeAspect={sizeAspect}
    />
  );
};
